// Pure routing/layout component — dispatches to the correct feature page based on
// the current URL path. Receives 80+ props from App.js; owns no business logic.

import React from 'react';
import * as Sentry from '@sentry/react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getViewFromPathname, legacyCrmRedirectTarget } from './ui/navigationConfig';
import { isMergeRoute } from '../utils/mergePaths';
import { templatesPath } from '../utils/templatesNavigation';
import { deriveFinancialFieldsForModule } from '../commissions/index';
import { isExpStint } from '../commissions/brokerage';
import { mergePeriodLeaseWithPatch } from '../commissions/pricing';
import { computePlanCurrentPeriodState } from '../commissions/modules/plan/projection';
import { planLeaderShareForChild } from '../commissions/modules/plan/teamSplit';
import { useBrokerage } from '../contexts/BrokerageContext';
import { usePlanPricingPeriod } from '../store/hooks/usePricingPeriod';
import { useTransactions, useTransactionsLoaded } from '../store/hooks/useTransactions';
import { resolveDealRouteState, findStoreDeal, needsTeamDealLookup } from '../utils/dealRouteState';
import { useTawDealLookup } from '../store/hooks/useTawDealLookup';
import DealNotFound, { DealLoading } from './features/transactions/DealNotFound';
import { useLeasedCollection } from '../store/hooks/useLeasedCollection';
import { useContactsData } from '../hooks/useContactsData';
import { useStageMoveToasts } from '../hooks/useStageMoveToasts';
import { useOptionalToast } from '../contexts/ToastContext';
import useStartDeal from '../hooks/useStartDeal';
import { useLenders } from '../store/hooks/useLenders';
import { buildStartedTransaction } from '../utils/startDealPrefill';
// D-08/D-09 (Plan 08, Task 2): the Reopen watcher below is the always-mounted
// consumer of a rejected primary write's registry entry — read-only from
// this file (publishing a write's fate stays in useTransactionHandlers.js,
// Plan 06). CR-01 fix (38-14-PLAN.md, gap closure): Reopen's onClick no
// longer calls `clear` itself — the reopened form's rehydration effect
// clears the entry AFTER consuming its payload, so the rehydration can
// never read a registry entry that was already gone. `clear` is used here
// ONLY by WR-03's onEvict path below — the same dismiss/cleanup primitive
// Plan 06's Retry toast already uses for its own kind: 'counters' entries —
// so an actionable toast evicted from the stack still reclaims its registry
// entry.
//
// CR-05 fix (38-15-PLAN.md, gap closure): onClick now also calls `update`
// to mark the entry `reopenRequested: true` instead of relying on Reopen's
// navigation to remount TransactionFormV2. handleUpdateTransaction never
// navigates away (useTransactionHandlers.js:377-397) — on an UPDATE the
// form stays mounted through the whole failure, and Reopen's rrNavigate
// lands on the SAME route the user is already on, which React Router does
// not remount. TransactionFormV2 already subscribes to this registry
// reactively (usePendingSaves, Plan 08 Task 1's beforeunload guard), so it
// re-renders on this exact mutation whether or not it remounts — marking
// the entry, not a one-shot mount effect, is what makes Reopen actually
// rehydrate the form on the path a real user takes.
import { usePendingSaves, update, clear } from '../hooks/usePendingSaves';
import TransactionsHeader from './features/transactions/TransactionsHeader';
import TransactionsArchivePage from './features/transactions/TransactionsArchivePage';
import TawPreviewDrawer from './features/transactions/v2/TawPreviewDrawer';
import StartTransactionModal from './features/transactions/StartTransactionModal';
import BusinessPartnersPage from './features/vendors/BusinessPartnersPage';
import MessageTemplatesPage from './features/messaging/MessageTemplatesPage';
import PlanSelection from './features/signup/PlanSelection';
const MessageComposerPage = React.lazy(() => import('./features/messaging/MessageComposerPage'));
const IncomeV2Page = React.lazy(() => import('./features/income/IncomeV2Page'));
const ExpensesV2Page = React.lazy(() => import('./features/expenses/ExpensesV2Page'));
const TransactionFormV2 = React.lazy(() => import('./features/transactions/TransactionFormV2'));
// BOOT-01 (Plan 36-03): the six heavy top-level routes, converted to
// React.lazy() so each ships as its own dynamic-import chunk excluded from
// the initial bundle. SettingsPage sequenced first per RESEARCH.md Open
// Question 2 (largest single split — 107 KB raw, bigger than the other five
// combined).
//
// GAP-4 (Plan 36-08, corrected 36-07): these were briefly moved into a
// retry-keyed React.useMemo() (36-07, GAP-1) so "Try again" could rebuild
// fresh React.lazy() instances after a chunk-load failure. That approach was
// unreliable — a browser caches a REJECTED dynamic-import promise in its
// module map, so re-import()ing the same chunk URL (even via a brand-new
// React.lazy() object) returns the cached rejection rather than re-fetching.
// Retry now reloads the tab instead (see RouteChunkErrorBoundary.js), so
// these are back to stable module-scope declarations.
const SettingsPage = React.lazy(() => import('./features/settings/SettingsPage'));
// Phase 66: Manage team (TawsTab, MembersTab, BillingTab, ChangePlanModal) was
// eager and pushed the startup bundle over budget; it loads on demand like the
// other heavy routes (todo 2026-10-12-lazy-load-manage-workspace).
const ManageWorkspacePage = React.lazy(() => import('./features/team/ManageWorkspacePage'));
const WhatsNewPage = React.lazy(() => import('./features/whatsNew/WhatsNewPage'));
const Dashboard = React.lazy(() => import('./features/dashboard/Dashboard'));
const TransactionListV2 = React.lazy(() => import('./features/transactions/TransactionListV2'));
const KanbanView = React.lazy(() => import('./features/transactions/KanbanView'));
const ClientsV2Page = React.lazy(() => import('./features/contacts/v2/ClientsV2Page'));
const TasksPage = React.lazy(() => import('./features/tasks/TasksPage'));
// Templates (owner decision, 2026-10-04): Email templates, Checklist
// templates, and Action plans on one page with a tab for each. Each tab body
// is its own lazy chunk inside TemplatesPage, so none of the three is in the
// main bundle.
const TemplatesPage = React.lazy(() => import('./features/templates/TemplatesPage'));
const ContactDetailPage = React.lazy(() => import('./features/contacts/v2/ContactDetailPage'));
// Phase 69: the merge screen is one lazy chunk, routed ahead of /crm/:id.
const MergeScreen = React.lazy(() => import('./features/contacts/v2/merge/MergeScreen'));
const AutomationsPageV2 = React.lazy(() => import('./features/automations/v2/AutomationsPageV2'));
// Phase 71 (D-40): the Calendar page, one lazy chunk so the main bundle budget holds.
const CalendarPage = React.lazy(() => import('./features/calendar/CalendarPage'));
const CampaignsPage = React.lazy(() => import('./features/campaigns/CampaignsPage'));
const CampaignBuilderPage = React.lazy(() => import('./features/campaigns/CampaignBuilderPage'));
import buildInfo from '../buildInfo.json';
import { attachClientChecklist, detachClientChecklist } from '../services/transactionService';

// DATA-04 (39-21, Task 1): the currentView gate for the all-years
// transactions lease (39-18's `{ scope: { kind: 'all' } }` scope). None of
// these is a boot view (dashboard/transactions/contacts). The archive
// sub-route (currentView === 'transactions', location.pathname ===
// '/transactions/archive') is gated separately via `isArchiveView`, since it
// shares 'transactions' with the boot-view list/kanban routes that must stay
// ungated. Exported so this phase's own test suite (39-21) can exercise the
// exact gate this component uses rather than duplicating the view-name list.
//
// DATA-05 (39): 'settings' was DELIBERATELY REMOVED. It used to be here so
// SettingsPage's DataTab CSV export could read the all-scope transactions off
// this route-level lease, but that made a transaction edit re-render the
// Settings tree even with the Data tab closed (the UAT-profiler violation).
// DataTab now leases its own transactions (useTransactions all-scope) at the
// leaf, so the subscription lives only where it is used. Re-adding 'settings'
// here would reintroduce the over-render; the export no longer depends on it.
export const LEGACY_TRANSACTIONS_VIEWS = new Set([
    'businessPartners',
    'messageComposer',
    'form',
]);

// DATA-04 (39-22, Task 1): the currentView gate for the on-demand
// all-contacts lease. Deliberately excludes 'transactions' -- the
// StartTransactionModal (the only contacts consumer that lives inside the
// 'transactions' boot view) is gated separately below via
// showStartTransactionModal (ORed into contactsGate), so simply visiting the
// transactions list/kanban view never opens this listener.
//
// DATA-05 (39): 'settings' was DELIBERATELY REMOVED here too, for the same
// reason as LEGACY_TRANSACTIONS_VIEWS above -- DataTab now leases its own
// contacts at the leaf, so the Settings route no longer opens this listener
// and a contact/transaction change no longer re-renders the Settings tree.
export const LEGACY_CONTACTS_VIEWS = new Set([
    'messageComposer',
    'form',
]);

export function AppRouter({
    workspaces,
    user,
    selectedWorkspaceId,
    selectedWorkspace,
    // The signed-in user's own account status, or null while
    // ensureUserAccount is still in flight. Gates the zero-workspace empty
    // state below -- see the comment there.
    accountStatus,
    // GAP-2 (Plan 36-07): true once useWorkspaceData's initial Firestore
    // fetch has resolved — threaded through to Dashboard, TransactionListV2,
    // and ClientsV2Page so each can show a loading skeleton instead of
    // flashing its empty-state CTA during the fetch.
    workspaceDataLoaded,
    // DATA-04 (39-14, Task 2 / 39-21): App.js no longer threads a `transactions`
    // collection prop (renamed, then fully retired) to AppRouter at all.
    // AppRouter leases ONLY its gated all-years slice via useTransactions()
    // below (allTransactions, LEGACY_TRANSACTIONS_VIEWS above --
    // BusinessPartnersPage, MessageComposerPage,
    // TransactionFormV2's allTransactions, TransactionsArchivePage's
    // multi-year browsing, and this router's own team-scope-enrichment /
    // resolvedTransaction-by-id lookup). The active-window slice that used to
    // live here too was moved into Dashboard (its only consumer) so
    // non-Dashboard routes -- Settings especially -- no longer re-render on a
    // transaction edit (DATA-05; see the note above the render body).
    statuses,
    customStatuses,
    isStatusManagerOpen,
    setIsStatusManagerOpen,
    handleEditClick,
    handleDeleteTransaction,
    handleSetAnniversaryMuted,
    handleMoveClick,
    isTcModeEnabled,
    handleOpenFinancials,
    handleBulkMoveTransactions,
    handleAddStatus,
    handleRenameStatus,
    handleDeleteStatus,
    handleSetStatusMinimized,
    handleReorderStatus,
    handleMoveStatusBefore,
    handleTransactionDrop,
    handleCreateIncomeType,
    handleUpdateIncomeType,
    handleDeleteIncomeType,
    handleMoveIncomeType,
    handleCreateWorkspaceIncome,
    handleUpdateWorkspaceIncome,
    handleDeleteWorkspaceIncome,
    setIncomeModalTrigger,
    incomeModalTrigger,
    handleCreateExpenseType,
    handleUpdateExpenseType,
    handleDeleteExpenseType,
    handleMoveExpenseType,
    handleCreateWorkspaceExpense,
    handleUpdateWorkspaceExpense,
    handleUpdateTransactionExpense,
    handleDeleteWorkspaceExpense,
    handleDeleteTransactionExpense,
    setExpenseModalTrigger,
    expenseModalTrigger,
    userSettings,
    handleSaveUserSettings,
    isSavingSettings,
    handleSaveWorkspaceAutomation,
    isSavingWorkspaceAutomation,
    workspaceDataEntrySettings,
    handleSaveWorkspaceDefaults,
    isSavingWorkspaceDefaults,
    isParentWorkspaceOwner,
    onUpdateChildPolicy,
    onUpdateChildSplit,
    onRemoveChildWorkspace,
    onOpenChildWorkspace,
    onRenameChild,
    onRemoveChildMember,
    expModuleSettings,
    onSaveExpModuleSettings,
    handleUploadWorkspaceLogo,
    handleDeleteWorkspaceLogo,
    handleUploadWorkspaceDarkLogo,
    handleDeleteWorkspaceDarkLogo,
    onRequestRenameWorkspace,
    onRequestDeleteWorkspace,
    handleUploadUserHeadshot,
    handleDeleteUserHeadshot,
    handleUploadUserBusinessCard,
    handleDeleteUserBusinessCard,
    // DATA-04 (39-22, Task 1): App.js no longer threads a contactsData
    // collection prop -- AppRouter leases its own gated on-demand
    // all-contacts slice below (leasedContacts/contactsData) and computes
    // contactsData locally via the same pure useContactsData hook App.js
    // used to call, fed by that lease + this router's own allTransactions.
    handleCreateContact,
    handleCreateDealContact,
    handleUpdateContact,
    handleDeleteWorkspaceContact,
    handleSyncContactFromGoogle,
    applyReviewedSync,
    applyResolvedSync,
    lenderHandlers,
    titleCompanyHandlers,
    realtorHandlers,
    companyListHandlers,
    vendorModalTrigger,
    realtorLinkedContactsConfig,
    handleCreateChecklistTemplate: _handleCreateChecklistTemplate,
    handleUpdateChecklistTemplate: _handleUpdateChecklistTemplate,
    handleDeleteChecklistTemplate: _handleDeleteChecklistTemplate,
    workspaceMembers,
    handleAddTransaction,
    handleCreateTransaction,
    handleDuplicateTransaction,
    selectedTransaction,
    handleUpdateTransaction,
    handleSearchGoogleContacts,
    transactionFormTab,
    showToast,
    // Team parent props
    childWorkspaces: taws,
    // T-41-CHIPS-STALE: App.js's SINGLE useTaws() instance's imperative
    // local-patch function -- threaded through to ManageWorkspacePage so its
    // split/policy/rename/member-removal wrappers patch the SAME roster
    // `taws` (above) is sourced from, instead of ManageWorkspacePage owning a
    // second, independent useTaws() call (see src/App.js's header comment
    // on this hook for the full history).
    patchTaw,
    teamScope,
    onScopeChange,
    childTransactions,
    onAssignTransactionToChild,
    onReassignTransaction,
    // TAW-03 (41-09, D-17): the lifted archive-year controls -- defaulted so
    // no existing caller/test breaks. Threaded into TransactionsArchivePage's
    // controlled yearFilter/onYearFilterChange props (Task 3).
    archiveYearFilter = 'all',
    // D-27: the announcement list/state for the /whats-new page.
    whatsNew = null,
    onArchiveYearFilterChange = () => {},
}) {
    const location = useLocation();
    const rrNavigate = useNavigate();
    // DATA-05 (39): the active-window transactions lease was MOVED OUT of
    // AppRouter and into Dashboard, its only consumer. Held here, it made a
    // transaction edit re-render AppRouter -- and therefore whatever route was
    // mounted, Settings included, which subscribes to nothing transaction-
    // related. Dashboard now leases the same active-window slice itself
    // (identical default scope + sliceKey, so the store refcount dedups to one
    // listener and the DATA-01 boot census stays 4), so the subscription only
    // exists while Dashboard is mounted. AppRouter no longer subscribes to any
    // transactions slice on non-Dashboard routes.
    // The form header's back button must behave EXACTLY like the browser back
    // button, which the transaction list's scroll-restore (Virtuoso
    // restoreStateFrom, only re-applied on a history POP) depends on. A plain
    // rrNavigate('/transactions') is a PUSH, which lands at the top of the list;
    // navigate(-1) is a POP, so the saved scroll position is restored. Fall back
    // to a push only when there is no in-app history to pop (deep link / refresh
    // straight onto the form, where location.key is still the initial 'default').
    const closeFormToList = React.useCallback(() => {
        if (location.key && location.key !== 'default') rrNavigate(-1);
        else rrNavigate('/transactions');
    }, [location.key, rrNavigate]);
    const currentView = getViewFromPathname(location.pathname);
    const transactionView =
        location.pathname === '/transactions/kanban' ? 'kanban'
        : location.pathname === '/transactions/archive' ? 'archive'
        : 'list';
    // Hoisted so both the all-scope lease gate below and the 'transactions'
    // switch case (which already used a local const of this exact name) can
    // share one computation.
    const isArchiveView = location.pathname === '/transactions/archive';
    // DATA-04 (39-21, Task 1): the gated all-years lease (39-18) feeding
    // every prior full-history consumer -- see LEGACY_TRANSACTIONS_VIEWS'
    // own comment above for the gating rationale. A real workspaceId is
    // passed only while a legacy-consumer view (or the archive sub-route)
    // is active; every other view gets `undefined`, which useTransactions
    // treats as "open zero listeners" (39-08's no-op contract), so boot
    // views never open this unbounded listener.
    const allTransactionsWorkspaceId = (LEGACY_TRANSACTIONS_VIEWS.has(currentView) || isArchiveView) ? selectedWorkspaceId : undefined;
    const allTransactions = useTransactions({
        scope: { kind: 'all' },
        workspaceId: allTransactionsWorkspaceId,
    });
    // Phase 55 Plan 39: the same gated slice's loaded signal, so a deal URL
    // that resolves to nothing shows "deal not found" only after the read
    // has landed (never while the listener is still in flight).
    const allTransactionsLoaded = useTransactionsLoaded({
        scope: { kind: 'all' },
        workspaceId: allTransactionsWorkspaceId,
    });

    // MEAS-02 (Phase 35, D-05): route-change timing. prevPathnameRef tracks the
    // pathname that was current when the last route-start timestamp was captured.
    // On the first render after a pathname change (i.e. once the new view has
    // committed), this effect measures the transition from the stored start
    // timestamp to now, attaches it to a Sentry span, then captures a fresh
    // start timestamp for the next transition. The very first mount captures a
    // start with nothing to measure against yet, which is correct (no prior
    // route to time from).
    //
    // WR-02: routeStartRef holds a per-transition performance.now() timestamp
    // instead of a global, singleton 'route-start'/'route-end' mark. Overlapping
    // or rapid navigations would otherwise let performance.measure pair marks
    // from different transitions, corrupting the duration. The local timestamp
    // plus the User Timing Level 3 options form keeps each measurement
    // self-contained. The measure name stays exactly 'route-duration' (the
    // baseline capture + Sentry span depend on it).
    const prevPathnameRef = React.useRef(location.pathname);
    const routeStartRef = React.useRef(null);
    // D-10 (Plan 36-03): a distinct cold-nav measure, separate from the
    // warm route-duration above. Fires once per view on its FIRST visit —
    // when the view's lazy chunk is actually downloading — then never again
    // for that same view. Keyed on `transactions:${transactionView}` (not a
    // bare currentView) so the transactions list and the Kanban board (both
    // currentView === 'transactions', different lazy chunks) register as
    // distinct first-visits and each gets its own cold sample.
    const visitedViewsRef = React.useRef(new Set());
    React.useEffect(() => {
        if (typeof performance === 'undefined'
            || typeof performance.now !== 'function'
            || typeof performance.measure !== 'function') return;
        if (prevPathnameRef.current !== location.pathname && routeStartRef.current !== null) {
            try {
                const measure = performance.measure('route-duration', { start: routeStartRef.current, end: performance.now() });
                const span = Sentry.startInactiveSpan({ name: 'route-duration' });
                span.setAttribute('duration_ms', measure.duration);
                span.end();
            } catch (err) {
                // never block the route change on a measurement failure.
            }

            const firstRouteKey = currentView === 'transactions'
                ? `transactions:${transactionView}`
                : currentView;
            if (!visitedViewsRef.current.has(firstRouteKey)) {
                try {
                    const firstMeasure = performance.measure('first-route-duration', { start: routeStartRef.current, end: performance.now() });
                    const firstSpan = Sentry.startInactiveSpan({ name: 'first-route-duration' });
                    firstSpan.setAttribute('duration_ms', firstMeasure.duration);
                    firstSpan.end();
                } catch (err) {
                    // never block the route change on a measurement failure.
                }
                visitedViewsRef.current.add(firstRouteKey);
            }
        }
        prevPathnameRef.current = location.pathname;
        routeStartRef.current = performance.now();
        // currentView/transactionView are pure derivations of location.pathname
        // (recomputed every render), so they are already in sync whenever this
        // effect runs; the dependency array intentionally stays
        // [location.pathname] per the existing WR-02 route-duration pattern
        // this effect extends.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // D-08 (Plan 08, Task 2): the Reopen watcher for a rejected primary
    // write. AppRouter is always mounted, so this watcher outlives
    // TransactionFormV2 — which is mandatory, because D-08's actual scenario
    // is a failure arriving AFTER the form that fired the save has already
    // unmounted (dismissed in <100ms, user navigated away before the ack
    // ever landed; T-38-31b). This reads the pending-saves registry
    // directly, never a promise: run() (useAsyncHandler.js) never
    // re-throws, so a registry transition to status: 'failed' on a
    // kind: 'primary-write' entry is the only wired signal (Plan 06 Task
    // 3's .then()/.catch() attached directly to the write's own promise,
    // outside run()).
    const pendingSaves = usePendingSaves();
    // T-38-33b: this effect re-runs on every registry mutation, including
    // unrelated kind: 'counters' transitions (Plan 06's own Retry toast) —
    // without this Set, an unguarded effect would re-raise the same Reopen
    // toast on every unrelated mutation, evicting other toasts from Plan
    // 01's 5-slot stack and burying the very failure it reports.
    const surfacedReopenIdsRef = React.useRef(new Set());
    React.useEffect(() => {
        pendingSaves.forEach((entry) => {
            // kind: 'counters' failures are Plan 06's Retry toast — ignore
            // them entirely here; only a rejected PRIMARY write gets a
            // Reopen offer.
            if (entry.kind !== 'primary-write' || entry.status !== 'failed') return;
            // A failed autosave (a create or an update) belongs to the deal form
            // that made it while that form is open and shows the deal: its own
            // status and Retry handle it. The form lets go when it moves to
            // another deal or unmounts, once its saves have settled, and an offer
            // then appears here if the failure still stands.
            if (entry.quiet && entry.ownedByForm) return;
            if (surfacedReopenIdsRef.current.has(entry.saveId)) return;
            surfacedReopenIdsRef.current.add(entry.saveId);

            // CR-01 fix (38-14-PLAN.md, gap closure): onClick used to call
            // clear(entry.saveId) HERE, before navigating — but the
            // reopened form's mount-effect rehydration is a one-shot read
            // of this exact registry entry, and it only mounts AFTER this
            // onClick returns. Clearing first meant the entry was already
            // gone by the time the form ever looked for it, so the
            // rehydration silently never fired and the toast's own promise
            // ("Your edits were not lost") was false on every single
            // Reopen click (38-REVIEW.md CR-01). The entry is now cleared
            // by the reopened form's own mount effect, immediately after
            // it has consumed failedEntry.payload. Only the surfaced-ids
            // bookkeeping (so this saveId doesn't re-raise a duplicate
            // toast, T-38-33b) belongs here on click.
            //
            // WR-03 fix (38-REVIEW.md, gap closure): if this toast is
            // evicted from the 5-slot stack instead of clicked
            // (ToastContext.js's evictOldestPreferringActionFree, only
            // reachable once every visible toast already carries an
            // action), onEvict is the cleanup-only counterpart — it must
            // NEVER navigate (the user never asked to go anywhere), it
            // only reclaims the registry entry so an evicted Reopen toast
            // doesn't leak the entry forever with no remaining path to
            // ever clear it.
            showToast(
                'Could not save the transaction. Your edits were not lost.',
                'error',
                {
                    // Task 3 fix (38-15-PLAN.md, gap closure): repeated
                    // failed saves of the SAME transaction must collapse
                    // into ONE toast, not stack a duplicate per attempt
                    // (human testing) — each save attempt gets its own
                    // registry entry/saveId (nextSaveRequestId in
                    // useTransactionHandlers.js), so surfacedReopenIdsRef
                    // alone (keyed by saveId) cannot prevent this. Scoped to
                    // this transaction's Reopen toast specifically — a
                    // DIFFERENT transaction's failure, or this SAME
                    // transaction's counters-sync failure (a different
                    // kind — Plan 06's Retry toast, which carries no
                    // dedupeKey), still coexist (D-06 intact). See
                    // ToastContext.js's showToast for the replace-not-stack
                    // mechanics, including how the replaced toast's own
                    // onEvict still reclaims its registry entry.
                    dedupeKey: `reopen:${entry.transactionId}`,
                    action: {
                        label: 'Reopen',
                        onClick: () => {
                            // The offer was withdrawn (its entry left the
                            // registry unclicked, see below): there is
                            // nothing to reopen, so the click only dismisses.
                            if (!surfacedReopenIdsRef.current.has(entry.saveId)) return;
                            surfacedReopenIdsRef.current.delete(entry.saveId);
                            // CR-05 fix (38-15-PLAN.md, gap closure): mark
                            // the entry rather than relying on this click's
                            // navigation to remount the form — see the
                            // import comment above for why a remount is not
                            // guaranteed (indeed, on the far more common
                            // UPDATE path, never happens at all). The
                            // reopened form's own reactive registry
                            // subscription is what actually performs the
                            // rehydration, once it observes this flag.
                            update(entry.saveId, { reopenRequested: true });
                            // T-38-30: this only navigates now — following
                            // the onDelete result-then-navigate template
                            // (:735-738) rather than inventing a new
                            // callback style. The state.initialTransaction
                            // shape mirrors handleStartTransaction's own
                            // client-minted-id navigation above so a
                            // reopened CREATE (never actually persisted —
                            // Firestore rolled it back before it ever
                            // landed) still resolves a transaction for the
                            // form to render, even though transactions.find()
                            // cannot find it. trackPrimaryWrite always
                            // carries a transactionId (Plan 04's
                            // client-minted-id contract applies to both
                            // create and update), so the "no id yet"
                            // fallback below is defensive only.
                            if (entry.transactionId) {
                                // reopenedSave: the rolled-back deal is not in
                                // the store by design, so the deal route keeps
                                // trusting this route state after the store
                                // has loaded (resolveDealRouteState).
                                rrNavigate(`/transactions/${entry.transactionId}`, {
                                    state: { initialTransaction: { id: entry.transactionId }, reopenedSave: true },
                                });
                            } else {
                                rrNavigate('/transactions/new');
                            }
                        },
                        onEvict: () => {
                            clear(entry.saveId);
                            surfacedReopenIdsRef.current.delete(entry.saveId);
                        },
                    },
                }
            );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingSaves, showToast, rrNavigate]);

    // A Reopen offer whose entry left the registry without a click has nothing
    // left to reopen: a later autosave of the same deal landed or replaced it (the
    // user came back to the deal, or a queued save stored it), or the user chose
    // to leave the edits behind. Withdraw the offer and take its toast down,
    // unless another failed save of the same deal still shares it.
    // reopenOffersRef records each offer's deal while its entry exists.
    const { toasts: visibleToasts, dismissToast } = useOptionalToast();
    const visibleToastsRef = React.useRef(visibleToasts);
    visibleToastsRef.current = visibleToasts;
    const reopenOffersRef = React.useRef(new Map());
    React.useEffect(() => {
        const withdrawn = [];
        surfacedReopenIdsRef.current.forEach((saveId) => {
            const entry = pendingSaves.get(saveId);
            if (entry) reopenOffersRef.current.set(saveId, entry.transactionId);
            else withdrawn.push(saveId);
        });
        withdrawn.forEach((saveId) => {
            const transactionId = reopenOffersRef.current.get(saveId);
            surfacedReopenIdsRef.current.delete(saveId);
            reopenOffersRef.current.delete(saveId);
            const stillOffered = [...surfacedReopenIdsRef.current]
                .some((id) => reopenOffersRef.current.get(id) === transactionId);
            if (stillOffered) return;
            visibleToastsRef.current
                .filter((toast) => toast.dedupeKey === `reopen:${transactionId}`)
                .forEach((toast) => dismissToast(toast.id));
        });
        // Offers clicked or evicted are no longer surfaced; drop their records.
        [...reopenOffersRef.current.keys()].forEach((saveId) => {
            if (!surfacedReopenIdsRef.current.has(saveId)) reopenOffersRef.current.delete(saveId);
        });
    }, [pendingSaves, dismissToast]);

    // Phase 55 (D-12): every contact stage move, single or bulk, raises one
    // Undo toast. Mounted once, app-wide.
    useStageMoveToasts({ showToast, actor: user });

    const [showStartTransactionModal, setShowStartTransactionModal] = React.useState(false);

    // DATA-04 (39-22, Task 1): the gated on-demand all-contacts lease feeding
    // every prior contactsData consumer (StartTransactionModal below, the
    // SettingsPage/DataTab CSV export, TransactionFormV2's contacts prop).
    // `contactsGate` ORs in
    // showStartTransactionModal (declared just above) rather than adding
    // 'transactions' to LEGACY_CONTACTS_VIEWS -- the modal lives inside the
    // 'transactions' BOOT view, so gating on the view name alone would open
    // this listener on every plain visit to the transactions list/kanban,
    // exceeding the D-16 four-listener boot census. Gating on the modal's
    // own open state instead means the lease opens only while the modal is
    // actually mounted. `leasedContacts` reuses useLeasedCollection (39-08)
    // -- the exact same generic per-route lease machine the D-20 collections
    // use -- and contactsData is computed via the same pure useContactsData
    // hook App.js used to call, fed by this lease plus the router's own
    // gated allTransactions (declared above), so its output is byte-for-byte
    // identical to what App.js used to thread down.
    const contactsGate = (LEGACY_CONTACTS_VIEWS.has(currentView) || showStartTransactionModal)
        ? selectedWorkspaceId
        : undefined;
    const leasedContacts = useLeasedCollection('contacts', contactsGate);
    const contactsData = useContactsData({ workspaceContacts: leasedContacts, transactions: allTransactions });

    // Phase 55 (D-23): Start a deal from a contact opens this same dialog,
    // pre-filled from the contact and its criteria.
    const startDealLenders = useLenders(showStartTransactionModal ? selectedWorkspaceId : undefined, selectedWorkspace);
    const { prefill: startDealPrefill, openStartDeal, closeStartDeal } = useStartDeal({
        contacts: contactsData,
        lenders: startDealLenders,
        setShowStartTransactionModal,
    });

    const handleStartTransaction = React.useCallback(async ({ transactionType, status, clients, financingType, budget, lenderIds, propertyStreet, propertyCityStateZip, listPrice }) => {
        const started = buildStartedTransaction({
            transactionType, status, clients, financingType, budget, lenderIds,
            propertyStreet, propertyCityStateZip, listPrice,
            workspaceDefaults: workspaceDataEntrySettings,
        });
        const newId = await handleCreateTransaction(started);
        if (newId) {
            closeStartDeal();
            rrNavigate(`/transactions/${newId}`, {
                state: { initialTransaction: { id: newId, ...started } }
            });
        }
    }, [handleCreateTransaction, rrNavigate, closeStartDeal, workspaceDataEntrySettings]);

    const handleAttachClientChecklist = React.useCallback(async (transactionId, transaction, template) => {
        if (!selectedWorkspaceId) return;
        return attachClientChecklist(selectedWorkspaceId, transactionId, transaction, template);
    }, [selectedWorkspaceId]);

    const handleDetachClientChecklist = React.useCallback(async (transactionId, transaction, templateId) => {
        if (!selectedWorkspaceId) return;
        await detachClientChecklist(selectedWorkspaceId, transactionId, transaction, templateId);
    }, [selectedWorkspaceId]);

    // A plan leader prices its share of each child deal with its own plan, at
    // its current cap-year state: its closed deals in the cap year (the period
    // lease, merged with the active window) plus the team-split records, which
    // the leader can read. Leased only while the team view shows child rows.
    const { brokerage } = useBrokerage();
    const isPlanLeader = Boolean(brokerage?.current) && !isExpStint(brokerage.current);
    // The deal route ('form') also shows a child deal in the preview drawer,
    // which prices the leader's share from this same state.
    const planTeamView = isPlanLeader && isParentWorkspaceOwner && (teamScope !== 'own' || currentView === 'form');
    const planPeriodTransactions = usePlanPricingPeriod(brokerage, expModuleSettings, planTeamView ? selectedWorkspaceId : null);
    const planTeamSplits = useLeasedCollection('workspaceIncome', planTeamView ? selectedWorkspaceId : undefined);
    const leaderTeamSplitRecords = React.useMemo(
        () => (planTeamView ? (planTeamSplits || []).filter(r => r?.source === 'team-split') : []),
        [planTeamView, planTeamSplits],
    );
    const leaderPlanState = React.useMemo(() => {
        if (!planTeamView) return null;
        return computePlanCurrentPeriodState(
            mergePeriodLeaseWithPatch(planPeriodTransactions, allTransactions || []),
            brokerage.current, new Date(), leaderTeamSplitRecords,
        );
    }, [planTeamView, leaderTeamSplitRecords, planPeriodTransactions, allTransactions, brokerage]);

    // Hoisted out of displayTransactions (below) so the teamTransactions memo
    // can share the exact same per-row derivation without duplicating it —
    // the eXp branch is unchanged line-for-line from the prior inline closure.
    const enrich = React.useCallback((t) => {
        if (leaderPlanState) {
            let _parentNetIncome = null;
            try {
                // Shared with the team preview drawer, so the row and the
                // drawer agree; a Sold row reads its stored record.
                const { derived } = planLeaderShareForChild(t, {
                    leaderTeamSplitPercentage: expModuleSettings?.teamSplitPercentage,
                    plan: brokerage.current.plan,
                    periodState: leaderPlanState,
                    teamSplitRecords: leaderTeamSplitRecords,
                });
                _parentNetIncome = derived ? Number(derived.netIncome) || null : null;
            } catch (_) { /* best-effort derivation; ignore */ }
            return { ...t, _parentNetIncome };
        }
        // Pre-compute the parent's actual net income for child rows so the list
        // can display real net rather than raw gross share.
        const splitPercentToAgent = Number(t._splitPercentToAgent ?? 100);
        const parentSharePercent = 100 - splitPercentToAgent;
        const splitBase = Number(t.commissionAfterReferral ?? t.grossCommission) || 0;
        const teamSplitPct = Number(expModuleSettings?.teamSplitPercentage) || 100;
        const combinedSplitPct = parentSharePercent * (teamSplitPct / 100);
        let _parentNetIncome = null;
        if (splitBase > 0 && parentSharePercent > 0) {
            try {
                const derived = deriveFinancialFieldsForModule(
                    {
                        ...t,
                        grossCommission: splitBase,
                        commissionInputMode: 'flat',
                        referralFee: 0,
                        adminFee: 0,
                        expenses: [],
                        // Ensure shouldZeroNet never fires for a valid flat commission.
                        // In flat mode listPrice/budget don't affect the gross calculation.
                        salePrice: Number(t.salePrice) || Number(t.listPrice) || Number(t.budget) || splitBase,
                    },
                    { ...expModuleSettings, teamSplitPercentage: combinedSplitPct }
                );
                _parentNetIncome = Number(derived.netIncome) || null;
            } catch (_) { /* best-effort derivation; ignore */ }
        }
        return { ...t, _parentNetIncome };
    }, [expModuleSettings, leaderPlanState, leaderTeamSplitRecords, brokerage]);

    // TAW-03 (41-06): the scoped rows TransactionListV2's TeamScopeFilter
    // actually needs, threaded as their own transactionListProps entries
    // (below) rather than through the `transactions` prop, which
    // TransactionListV2 deliberately does not consume (DATA-04). Mirrors
    // displayTransactions' own three-way branch so the two never diverge.
    const includeOwnTransactions = !isParentWorkspaceOwner || teamScope === 'own' || teamScope === 'all';

    const teamTransactions = React.useMemo(() => {
        if (!isParentWorkspaceOwner || teamScope === 'own') return [];
        if (teamScope === 'all') return (childTransactions || []).map(enrich);
        // Specific child selected — show only that child's transactions. The
        // childTransactions array now always contains every TAW's deals (the
        // hook no longer filters by scope), so apply the scope filter here.
        return (childTransactions || [])
            .filter(t => t._workspaceId === teamScope)
            .map(enrich);
    }, [isParentWorkspaceOwner, teamScope, childTransactions, enrich]);

    // Build the transaction list based on team scope. Still the sole source
    // for TransactionsArchivePage (transactionListProps.transactions, :680) —
    // composes from teamTransactions/includeOwnTransactions above instead of
    // duplicating the enrich mapping, but its returned VALUE (including exact
    // reference identity for the 'own'/non-parent case) and its reactivity to
    // the same underlying inputs are unchanged.
    const displayTransactions = React.useMemo(() => {
        if (isParentWorkspaceOwner && teamScope === 'all') {
            return [
                ...allTransactions.map(t => ({ ...t, _workspaceName: undefined })),
                ...teamTransactions,
            ];
        }
        return includeOwnTransactions ? allTransactions : teamTransactions;
    }, [isParentWorkspaceOwner, teamScope, allTransactions, teamTransactions, includeOwnTransactions]);

    // Child transactions open in a read-only parent-perspective view (no workspace switch).
    const handleEditWithChildGuard = React.useCallback((transaction) => {
        if (transaction._workspaceId) {
            rrNavigate(`/transactions/${transaction.id}`, {
                state: { parentViewChildTransaction: transaction },
            });
            return;
        }
        handleEditClick(transaction);
    }, [handleEditClick, rrNavigate]);

    const urlTransactionId = (() => {
        const match = location.pathname.match(/^\/transactions\/([^/]+)$/);
        if (!match) return null;
        return match[1] === 'new' ? null : match[1];
    })();
    const isNewTransactionRoute = location.pathname === '/transactions/new';
    // Only use selectedTransaction if it matches the current URL — prevents stale
    // selectedTransaction from a previously-opened form blocking the read-only view.
    const selectedMatch = selectedTransaction?.id === urlTransactionId ? selectedTransaction : null;
    // The deal as found in the STORE. A parent owner's team (TAW) deal is
    // looked up in childTransactions regardless of the list's team-scope
    // filter, so a deep link to it is not judged by what the list shows.
    const storeTransactionWithoutLookup = (isNewTransactionRoute || !urlTransactionId)
        ? null
        : (allTransactions.find(t => t.id === urlTransactionId)
            ?? findStoreDeal({
                urlTransactionId,
                displayTransactions,
                childTransactions,
                isParentWorkspaceOwner,
                enrich,
            }));
    // Under the default 'own' team scope the store holds no team rows at all,
    // so a member's deal reached by link (no route state) is found by a
    // one-shot read in each team agent workspace. Its `loaded` flag is the
    // team half of the not-found judgement below. It opens no listener, and
    // never runs for a non-team workspace (needsTeamDealLookup).
    const teamDealLookup = useTawDealLookup({
        dealId: urlTransactionId,
        tawIds: selectedWorkspace?.teamAgentWorkspaceIds,
        taws,
        enabled: !isNewTransactionRoute && needsTeamDealLookup({
            urlTransactionId,
            isParentWorkspaceOwner,
            allLoaded: allTransactionsLoaded,
            storeDeal: storeTransactionWithoutLookup,
            routeStateTransaction: location.state?.parentViewChildTransaction
                || location.state?.initialTransaction
                || null,
            routeStateTrusted: Boolean(location.state?.reopenedSave),
        }),
    });
    const storeTransaction = storeTransactionWithoutLookup
        ?? ((isNewTransactionRoute || !teamDealLookup.deal)
            ? null
            : findStoreDeal({
                urlTransactionId,
                isParentWorkspaceOwner,
                teamLookupDeal: teamDealLookup.deal,
                enrich,
            }));
    const resolvedTransaction = isNewTransactionRoute
        ? null
        : (selectedMatch
            || storeTransaction
            || location.state?.parentViewChildTransaction
            || location.state?.initialTransaction
            || null);
    // Phase 55 Plan 39: a transaction handed over outside the store (a just
    // created deal, a parent-view child, or the clicked row) stands in until
    // the store listener delivers it. After that, resolveDealRouteState trusts
    // it only for a team child or a reopened failed save, so stale history
    // state for a deleted deal never reopens an editable form.
    const routeStateTransaction = selectedMatch
        || location.state?.initialTransaction
        || location.state?.parentViewChildTransaction
        || null;

    // `accountStatus !== null` means ensureUserAccount has answered. Without
    // it this empty state renders for EVERY brand-new signup during the
    // provisioning round trip -- and PlanSelection's CTAs would call
    // createCheckoutSession or the Free-provisioning callable before the
    // userAccount document they depend on exists.
    //
    // D-02 (50-CONTEXT.md): this is PlanSelection's embedded mount, replacing
    // the old "Create your first team" dead end. No wrapper div here -- the
    // parent <main> in App.js already supplies a correct scroll container
    // (flex-1 min-h-0 overflow-y-auto), so a second one would nest two scroll
    // regions.
    if (workspaces.length === 0 && user && accountStatus !== null
        && currentView !== 'settings' && currentView !== 'manageWorkspace') {
        return <PlanSelection chrome="embedded" />;
    }
    const viewsNeedingWorkspace = new Set(['dashboard', 'transactions', 'contacts', 'lenders', 'titleCompanies', 'realtors', 'templates', 'tasks', 'messageTemplates', 'income', 'expenses', 'form', 'manageWorkspace', 'automations', 'campaigns', 'calendar']);
    if (viewsNeedingWorkspace.has(currentView) && !selectedWorkspaceId) {
        return <div className="text-center p-10">Please select a team.</div>;
    }

    // The start dialog mounts on both the transactions view and the contacts
    // views, since Start a deal is launched from the Clients board, the Clients
    // list, and the contact page (Phase 55, D-23). The contact lease loads once
    // the dialog opens, so a co-client (D-21) can arrive after the first
    // render; the dialog merges it in itself. The key changes only with the
    // contact, never with the co-client count, so a late co-client does not
    // remount the dialog and drop what the user entered.
    const startTransactionModalElement = showStartTransactionModal ? (
        <StartTransactionModal
            key={startDealPrefill ? `deal-${startDealPrefill.contact?.id || ''}` : 'plain'}
            statuses={statuses}
            workspaceDefaults={workspaceDataEntrySettings}
            contacts={contactsData || []}
            onUpdateContact={handleUpdateContact}
            // D-14: a contact created from the deal form records consent basis 'deal'.
            onCreateContact={handleCreateDealContact || handleCreateContact}
            onSyncContactFromGoogle={handleSyncContactFromGoogle}
            applyReviewedSync={applyReviewedSync}
            applyResolvedSync={applyResolvedSync}
            onSearchGoogleContacts={handleSearchGoogleContacts}
            workspaceId={selectedWorkspaceId}
            user={user}
            onCancel={closeStartDeal}
            onStartTransaction={handleStartTransaction}
            prefill={startDealPrefill}
        />
    ) : null;

    // Old Clients paths (/contacts*, /clients) redirect to /crm, keeping the
    // subpath, query, and hash (Phase 68, D-26).
    const legacyCrmTarget = legacyCrmRedirectTarget(location);
    if (legacyCrmTarget) return <Navigate to={legacyCrmTarget} replace />;

    switch (currentView) {
        case 'dashboard': return <Dashboard workspaceDataLoaded={workspaceDataLoaded} workspaceId={selectedWorkspaceId} selectedWorkspace={selectedWorkspace} allStatuses={statuses} onEditTransaction={handleEditClick} onUpdateTransaction={handleUpdateTransaction} />;
        case 'transactions': {
            const isListView = transactionView === 'list';
            // isArchiveView is now hoisted to the top of the component (feeds
            // the allTransactions lease gate above) -- reused here, not
            // redeclared.
            const handleSelectListView = () => {
                if (location.pathname !== '/transactions') {
                    rrNavigate('/transactions');
                }
            };
            const handleSelectBoardView = () => {
                if (location.pathname !== '/transactions/kanban') {
                    rrNavigate('/transactions/kanban');
                    setIsStatusManagerOpen(false);
                }
            };

            return (
                <div className="flex h-full flex-col overflow-hidden">
                    {transactionView === 'kanban' && (
                        <div className="flex-shrink-0 border-b border-[color:var(--border-color)] pb-4">
                            <TransactionsHeader
                                activeView={transactionView}
                                onSelectListView={handleSelectListView}
                                onSelectBoardView={handleSelectBoardView}
                                onCreateTransaction={() => setShowStartTransactionModal(true)}
                                onToggleStatusManager={() => setIsStatusManagerOpen(prev => !prev)}
                                canManageStatuses={false}
                                isStatusManagerOpen={false}
                            />
                        </div>
                    )}
                    <div className={`flex-1 min-h-0 ${transactionView === 'kanban' ? 'pt-4' : 'pt-0'}`}>
                        {(() => {
                            const transactionListProps = {
                                transactions: displayTransactions,
                                statuses,
                                customStatuses,
                                minimizedStatuses: selectedWorkspace?.minimizedStatuses || [],
                                onEdit: handleEditWithChildGuard,
                                onDelete: handleDeleteTransaction,
                                onMove: handleMoveClick,
                                // D-13: mirrors the MoveTransactionModal list in App.js. `workspaces` is
                                // the switcher list, which already excludes Team Agent workspaces, so Move
                                // never targets one; Assign is the one entry point for that.
                                hasMoveTargets: workspaces.some((ws) => ws.id !== selectedWorkspaceId),
                                onOpenFinancials: isTcModeEnabled ? null : handleOpenFinancials,
                                onBulkMove: handleBulkMoveTransactions,
                                onAddStatus: handleAddStatus,
                                onRenameStatus: handleRenameStatus,
                                onDeleteStatus: handleDeleteStatus,
                                onSetStatusMinimized: handleSetStatusMinimized,
                                showStatusManager: isStatusManagerOpen,
                                onReorderStatus: handleReorderStatus,
                                onMoveStatusBefore: handleMoveStatusBefore,
                                onToggleStatusManager: () => setIsStatusManagerOpen(prev => !prev),
                                onCreateTransaction: () => setShowStartTransactionModal(true),
                                onSelectListView: handleSelectListView,
                                onSelectBoardView: handleSelectBoardView,
                                activeView: transactionView,
                                canViewFinancials: !isTcModeEnabled,
                                isParentWorkspace: isParentWorkspaceOwner,
                                selectedWorkspaceId,
                                childWorkspaces: taws || [],
                                teamScope: teamScope || 'own',
                                onScopeChange,
                                teamTransactions,
                                includeOwnTransactions,
                                onAssignToChild: onAssignTransactionToChild,
                                onReassignTransaction,
                                expModuleSettings,
                                // D-20 (39-17, Task 1): workspaceIncome was threaded here but
                                // never consumed by TransactionListV2 -- dead prop-threading,
                                // removed outright (Rule 1 cleanup) now that workspaceIncome is
                                // no longer an AppRouter-level variable.
                                // GAP-2 (Plan 36-07): gates the loading skeleton vs. the
                                // "No transactions yet" empty CTA in TransactionListV2.
                                workspaceDataLoaded,
                            };
                            if (isArchiveView) {
                                // TAW-03 (41-09, D-17): transactions sources
                                // OWN rows from allTransactions, NOT
                                // transactionListProps.transactions
                                // (displayTransactions). displayTransactions
                                // already composes own + team rows (41-06
                                // Task 2), so forwarding both it AND
                                // teamTransactions would double-render every
                                // TAW row. allTransactions is the TW's own
                                // all-years lease; teamScope cannot leave
                                // 'own' on main today, and displayTransactions'
                                // own-branch returns allTransactions BY
                                // REFERENCE, so this substitution is
                                // behaviorally identical on main.
                                return (
                                    <TransactionsArchivePage
                                        transactions={allTransactions}
                                        statuses={transactionListProps.statuses}
                                        canViewFinancials={transactionListProps.canViewFinancials}
                                        currentYear={new Date().getFullYear()}
                                        onEdit={transactionListProps.onEdit}
                                        onBulkMove={transactionListProps.onBulkMove}
                                        onBack={() => rrNavigate('/transactions')}
                                        teamTransactions={transactionListProps.teamTransactions}
                                        includeOwnTransactions={transactionListProps.includeOwnTransactions}
                                        isParentWorkspace={transactionListProps.isParentWorkspace}
                                        childWorkspaces={transactionListProps.childWorkspaces}
                                        teamScope={transactionListProps.teamScope}
                                        onScopeChange={transactionListProps.onScopeChange}
                                        workspaceId={selectedWorkspaceId}
                                        yearFilter={archiveYearFilter}
                                        onYearFilterChange={onArchiveYearFilterChange}
                                    />
                                );
                            }
                            if (isListView) {
                                return (
                                    <TransactionListV2
                                        {...transactionListProps}
                                        onNavigateToArchive={() => rrNavigate('/transactions/archive')}
                                    />
                                );
                            }
                            return (
                                <div className="flex h-full flex-col overflow-hidden rounded-lg bg-[color:var(--surface-primary)] shadow-md">
                                    <div className="flex-1 min-h-0 overflow-auto">
                                        <KanbanView statuses={statuses} onDrop={handleTransactionDrop} onEdit={handleEditClick} selectedWorkspaceId={selectedWorkspaceId} />
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                    {startTransactionModalElement}
                </div>
            );
        }
        case 'income': {
            return (
                <IncomeV2Page
                    // DATA-04 (39-14, Task 2): IncomeV2Page reads its own
                    // range/active leases via useTransactions (39-13) --
                    // workspaceId wiring was explicitly left for this plan
                    // (39-13 SUMMARY, Next Phase Readiness). Without it, both
                    // of IncomeV2Page's internal leases stay no-ops (empty
                    // array) and the route renders zero income data.
                    workspaceId={selectedWorkspaceId}
                    onCreateIncomeType={handleCreateIncomeType}
                    onUpdateIncomeType={handleUpdateIncomeType}
                    onDeleteIncomeType={handleDeleteIncomeType}
                    onMoveIncomeType={handleMoveIncomeType}
                    onCreateIncome={handleCreateWorkspaceIncome}
                    onUpdateIncome={handleUpdateWorkspaceIncome}
                    onDeleteIncome={handleDeleteWorkspaceIncome}
                    onOpenRecordModal={() => setIncomeModalTrigger(prev => prev + 1)}
                    incomeModalTrigger={incomeModalTrigger}
                    expModuleSettings={expModuleSettings}
                    tawTransactions={childTransactions}
                />
            );
        }
        case 'expenses': {
            return (
                <ExpensesV2Page
                    // DATA-04 (39-14, Task 2): same wiring as IncomeV2Page
                    // above -- ExpensesV2Page's own useTransactions leases
                    // (39-13) are no-ops until workspaceId is threaded.
                    workspaceId={selectedWorkspaceId}
                    onCreateExpenseType={handleCreateExpenseType}
                    onUpdateExpenseType={handleUpdateExpenseType}
                    onDeleteExpenseType={handleDeleteExpenseType}
                    onMoveExpenseType={handleMoveExpenseType}
                    onCreateExpense={handleCreateWorkspaceExpense}
                    onUpdateExpense={handleUpdateWorkspaceExpense}
                    onUpdateTransactionExpense={handleUpdateTransactionExpense}
                    onDeleteExpense={handleDeleteWorkspaceExpense}
                    onDeleteTransactionExpense={handleDeleteTransactionExpense}
                    onOpenRecordModal={() => setExpenseModalTrigger(prev => prev + 1)}
                    expenseModalTrigger={expenseModalTrigger}
                    expModuleSettings={expModuleSettings}
                    tawTransactions={childTransactions}
                />
            );
        }
        case 'manageWorkspace':
            // D-14: owner-only gate removed intentionally — all team members get full access.
            // This is a deliberate, temporary relaxation. Re-gate to owner-only in a future phase.
            return (
                <ManageWorkspacePage
                    selectedWorkspaceId={selectedWorkspaceId}
                    selectedWorkspace={selectedWorkspace}
                    user={user}
                    contacts={contactsData}
                    taws={taws}
                    patchTaw={patchTaw}
                    onUpdateChildPolicy={onUpdateChildPolicy}
                    onUpdateChildSplit={onUpdateChildSplit}
                    onRemoveChildWorkspace={onRemoveChildWorkspace}
                    onOpenChildWorkspace={onOpenChildWorkspace}
                    onRenameChild={onRenameChild}
                    onRemoveChildMember={onRemoveChildMember}
                    onUploadWorkspaceLogo={handleUploadWorkspaceLogo}
                    onDeleteWorkspaceLogo={handleDeleteWorkspaceLogo}
                    onUploadWorkspaceDarkLogo={handleUploadWorkspaceDarkLogo}
                    onDeleteWorkspaceDarkLogo={handleDeleteWorkspaceDarkLogo}
                    onRequestRenameWorkspace={onRequestRenameWorkspace}
                    onRequestDeleteWorkspace={onRequestDeleteWorkspace}
                    workspaceDefaults={workspaceDataEntrySettings}
                    onSaveWorkspaceDefaults={handleSaveWorkspaceDefaults}
                    isSavingWorkspaceDefaults={isSavingWorkspaceDefaults}
                    statuses={statuses}
                    showToast={showToast}
                />
            );
        case 'settings':
            return (
                <SettingsPage
                    settings={userSettings}
                    onSave={handleSaveUserSettings}
                    isSaving={isSavingSettings}
                    workspaceDefaults={workspaceDataEntrySettings}
                    onSaveWorkspaceDefaults={handleSaveWorkspaceDefaults}
                    isSavingWorkspaceDefaults={isSavingWorkspaceDefaults}
                    expModuleSettings={expModuleSettings}
                    onSaveExpModuleSettings={onSaveExpModuleSettings}
                    statuses={statuses}
                    buildInfo={buildInfo}
                    workspace={selectedWorkspace}
                    onUploadUserHeadshot={handleUploadUserHeadshot}
                    onDeleteUserHeadshot={handleDeleteUserHeadshot}
                    onUploadUserBusinessCard={handleUploadUserBusinessCard}
                    onDeleteUserBusinessCard={handleDeleteUserBusinessCard}
                    user={user}
                    selectedWorkspaceId={selectedWorkspaceId}
                />
            );
        case 'contacts':
            // /crm/merge?a=&b= is the merge screen (Phase 69), checked before the
            // /crm/:id branch (see isMergeRoute for the 'merge' id edge).
            if (isMergeRoute(location.pathname, location.search)) {
                return <MergeScreen workspaceId={selectedWorkspaceId} />;
            }
            // /crm/:id[/<tab>] is the routed contact page (Phase 55, D-26).
            if (location.pathname.startsWith('/crm/')) {
                return (
                    <>
                    <ContactDetailPage
                        onStartDeal={openStartDeal}
                        workspaceDataLoaded={workspaceDataLoaded}
                        onUpdateContact={handleUpdateContact}
                        onDeleteContact={handleDeleteWorkspaceContact}
                        onSearchGoogleContacts={handleSearchGoogleContacts}
                        onSyncContactFromGoogle={handleSyncContactFromGoogle}
                        applyReviewedSync={applyReviewedSync}
                        applyResolvedSync={applyResolvedSync}
                        workspaceId={selectedWorkspaceId}
                        selectedWorkspace={selectedWorkspace}
                        lenderHandlers={lenderHandlers}
                        companyListHandlers={companyListHandlers}
                        user={user}
                    />
                    {startTransactionModalElement}
                    </>
                );
            }
            // The V2 Clients page is the only Clients page — every workspace renders it
            // unconditionally. The V1 page and its toggle were fully removed.
            return (
                <>
                <ClientsV2Page
                    onStartDeal={openStartDeal}
                    workspaceDataLoaded={workspaceDataLoaded}
                    onCreateContact={handleCreateContact}
                    onSetAnniversaryMuted={handleSetAnniversaryMuted}
                    onDeleteContact={handleDeleteWorkspaceContact}
                    onSearchGoogleContacts={handleSearchGoogleContacts}
                    workspaceId={selectedWorkspaceId}
                    workspace={selectedWorkspace}
                    user={user}
                />
                {startTransactionModalElement}
                </>
            );
        case 'businessPartners':
            return (
                <BusinessPartnersPage
                    workspaceId={selectedWorkspaceId}
                    selectedWorkspace={selectedWorkspace}
                    lenderHandlers={lenderHandlers}
                    titleCompanyHandlers={titleCompanyHandlers}
                    realtorHandlers={realtorHandlers}
                    companyListHandlers={companyListHandlers}
                    vendorModalTrigger={vendorModalTrigger}
                    realtorLinkedContactsConfig={realtorLinkedContactsConfig}
                    onSearchGoogleContacts={handleSearchGoogleContacts}
                    allowDelete={selectedWorkspace?.teamRole !== 'team_agent'}
                    transactions={allTransactions}
                    workspaceName={selectedWorkspace?.name || ''}
                />
            );
        case 'templates': return (
            <TemplatesPage
                workspaceId={selectedWorkspaceId}
                selectedWorkspace={selectedWorkspace}
                user={user}
                showToast={showToast}
                enabledLanguages={selectedWorkspace?.enabledLanguages ?? [{ code: 'en', name: 'English' }]}
                emailTabProps={{
                    defaultSort: userSettings?.emailTemplatesDefaultSort || 'name',
                    defaultGroup: userSettings?.emailTemplatesDefaultGroup || 'audience',
                    onSaveGridDefaults: (prefs) => handleSaveUserSettings({
                        ...(prefs.sort ? { emailTemplatesDefaultSort: prefs.sort } : {}),
                        ...(prefs.group ? { emailTemplatesDefaultGroup: prefs.group } : {}),
                    }),
                }}
                marketingEmailTabProps={{
                    defaultSort: userSettings?.emailMarketingTemplatesDefaultSort || 'name',
                    defaultGroup: userSettings?.emailMarketingTemplatesDefaultGroup || 'campaign',
                    onSaveGridDefaults: (prefs) => handleSaveUserSettings({
                        ...(prefs.sort ? { emailMarketingTemplatesDefaultSort: prefs.sort } : {}),
                        ...(prefs.group ? { emailMarketingTemplatesDefaultGroup: prefs.group } : {}),
                    }),
                }}
            />
        );
        case 'tasks': return (
            <TasksPage
                workspaceId={selectedWorkspaceId}
                selectedWorkspace={selectedWorkspace}
                user={user}
                showToast={showToast}
            />
        );
        case 'messageTemplates': return (
            <MessageTemplatesPage
                workspaceId={selectedWorkspaceId}
                showToast={showToast}
            />
        );
        case 'messageComposer': return (
            <MessageComposerPage
                workspaceId={selectedWorkspaceId}
                workspace={selectedWorkspace ? { ...selectedWorkspace, id: selectedWorkspaceId } : null}
                transactions={allTransactions}
            />
        );
        case 'whatsNew': return (
            <WhatsNewPage
                helpList={whatsNew?.helpList}
                error={whatsNew?.error}
                loading={whatsNew?.loading}
            />
        );
        case 'automations': return (
            <AutomationsPageV2
                user={user}
                selectedWorkspace={selectedWorkspace ? { ...selectedWorkspace, id: selectedWorkspaceId } : null}
                onSaveWorkspaceAutomation={handleSaveWorkspaceAutomation}
                isSavingWorkspaceAutomation={isSavingWorkspaceAutomation}
            />
        );
        case 'calendar': return <CalendarPage workspaceId={selectedWorkspaceId} />;
        case 'campaigns': {
            // /campaigns/new and /campaigns/:id are the full-page builder (Phase 67).
            const builderMatch = /^\/campaigns\/([^/]+)\/?$/.exec(location.pathname);
            if (builderMatch) {
                let campaignId = builderMatch[1];
                try { campaignId = decodeURIComponent(campaignId); } catch { /* keep the raw segment */ }
                return <CampaignBuilderPage workspaceId={selectedWorkspaceId} campaignId={campaignId} selectedWorkspace={selectedWorkspace ? { ...selectedWorkspace, id: selectedWorkspaceId } : null} />;
            }
            return <CampaignsPage workspaceId={selectedWorkspaceId} selectedWorkspace={selectedWorkspace || null} />;
        }
        case 'form': {
            if (resolvedTransaction?._workspaceId) {
                return (
                    <TawPreviewDrawer
                        open
                        transaction={resolvedTransaction}
                        expModuleSettings={expModuleSettings}
                        leaderPlanState={leaderPlanState}
                        teamSplitRecords={leaderTeamSplitRecords}
                        onOpenInWorkspace={() => {
                            onOpenChildWorkspace(resolvedTransaction._workspaceId);
                            rrNavigate(`/transactions/${resolvedTransaction.id}`);
                        }}
                        onClose={closeFormToList}
                    />
                );
            }
            const dealRouteState = resolveDealRouteState({
                urlTransactionId,
                resolvedTransaction: storeTransaction,
                routeStateTransaction,
                allLoaded: allTransactionsLoaded,
                // A parent owner's id is missing only once the team read has
                // also loaded; until then the route shows DealLoading.
                isParentWorkspaceOwner: Boolean(isParentWorkspaceOwner),
                teamLoaded: teamDealLookup.loaded,
                routeStateTrusted: Boolean(location.state?.reopenedSave),
            });
            if (dealRouteState === 'not-found') {
                return <DealNotFound onBack={() => rrNavigate('/transactions')} />;
            }
            if (dealRouteState === 'loading') {
                return <DealLoading />;
            }
            return <TransactionFormV2
                                onSubmit={handleAddTransaction}
                                initialData={resolvedTransaction}
                                routeTransactionId={urlTransactionId}
                                onUpdate={handleUpdateTransaction}
                                allTransactions={allTransactions}
                                allTransactionsLoaded={allTransactionsLoaded}
                                onDuplicate={handleDuplicateTransaction}
                                onDelete={async (id, txn) => {
                                    const ok = await handleDeleteTransaction(id, txn);
                                    if (ok) rrNavigate('/transactions');
                                    // The form reads anything but true as a failed delete.
                                    return ok === true;
                                }}
                                statuses={statuses}
                                onAddStatus={handleAddStatus}
                                onClose={closeFormToList}
                                onNavigateToTemplates={() => rrNavigate(templatesPath('transaction'))}
                                vendorHandlers={{
                                    lenders: lenderHandlers,
                                    titleCompanies: titleCompanyHandlers,
                                    realtors: realtorHandlers
                                }}
                                companyListHandlers={companyListHandlers}
                                realtorLinkedContactsConfig={realtorLinkedContactsConfig}
                                contacts={contactsData}
                                onUpdateContact={handleUpdateContact}
                                // D-14: contacts created from the deal form record consent basis 'deal'.
                                onCreateContact={handleCreateDealContact || handleCreateContact}
                                onSyncContactFromGoogle={handleSyncContactFromGoogle}
                                applyReviewedSync={applyReviewedSync}
                                applyResolvedSync={applyResolvedSync}
                                onSearchGoogleContacts={handleSearchGoogleContacts}
                                onCreateExpenseType={handleCreateExpenseType}
                                expModuleSettings={expModuleSettings}
                                defaultTab={isTcModeEnabled ? 'details' : transactionFormTab}
                                workspaceDefaults={workspaceDataEntrySettings}
                                showFinancialsTab={!isTcModeEnabled}
                                workspaceId={selectedWorkspaceId}
                                workspaceMembers={workspaceMembers}
                                onNotify={({ type = 'info', message }) => showToast(message, type)}
                                workspace={selectedWorkspace}
                                defaultSignatureId={userSettings.defaultSignatureId}
                                onAttachClientChecklist={handleAttachClientChecklist}
                                onDetachClientChecklist={handleDetachClientChecklist}
                                // Phase 7: Checklists + Email Composer (CHECK-* + EMAIL-* props).
                                selectedWorkspace={selectedWorkspace}
                                userSettings={userSettings}
                                showToast={showToast}
                            />;
        }
        default: return <Dashboard workspaceDataLoaded={workspaceDataLoaded} workspaceId={selectedWorkspaceId} selectedWorkspace={selectedWorkspace} allStatuses={statuses} onEditTransaction={handleEditClick} onUpdateTransaction={handleUpdateTransaction} />;
    }
}
